import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast, { Toaster } from "react-hot-toast";
import {
  ArrowRight,
  Bell,
  CreditCard,
  Layers,
  Rocket,
  Sparkles,
  Users,
} from "lucide-react";
import { startTrial } from "../../../api/General/PlatformTerms";
import { track, AnalyticsEvents } from "../../../utils/analytics";
import { Checkbox } from "../../../components/ui/Checkbox";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import Button from "../../../components/ui/Button";
import TermsDialog from "../../../components/terms/TermsDialog";
import { Error as ApiError } from "../../../models/general";

/**
 * StartTrial — authenticated upgrade flow that converts a Free company into a
 * 30-day Trial. Reached from the Phase 4 "Upgrade" banner (and a direct link
 * for HR/Admin). The Free → Trial pre-flight is enforced by the backend; this
 * page assumes it has been routed to legitimately.
 *
 * Design notes (impeccable: distill):
 *   - Single calm card. No nested cards. No big-number hero.
 *   - Benefit list reads as a paragraph, not a marketing block.
 *   - Optional fields stay collapsed under "Help us tailor it" so the primary
 *     CTA isn't buried behind a long form.
 */

const SIZE_OPTIONS = [
  { value: "1-10", label: "1–10" },
  { value: "11-50", label: "11–50" },
  { value: "51-200", label: "51–200" },
  { value: "201-1000", label: "201–1,000" },
  { value: "1000+", label: "1,000+" },
];

const StartTrialPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRtl = i18n.dir() === "rtl";

  const [industry, setIndustry] = useState<string>("");
  const [size, setSize] = useState<string>("");
  const [reference, setReference] = useState<string>("");
  const [accepted, setAccepted] = useState<boolean>(false);
  const [termsOpen, setTermsOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const referralOptions = useMemo(
    () => [
      { value: "Google", label: t("Google") },
      { value: "Whatsapp", label: t("Whatsapp") },
      { value: "Facebook", label: t("Facebook") },
      { value: "Instagram", label: t("Instagram") },
      { value: "Word of Mouth", label: t("Word of Mouth") },
      { value: "In-Person Event", label: t("In-Person Event") },
      { value: "Partner Company", label: t("Partner Company") },
      { value: "Other", label: t("Other") },
    ],
    [t],
  );

  // Confetti redirect after 1.5s
  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => {
      navigate("/", { replace: true });
      toast.success(t("Trial started — every module is unlocked for 30 days."));
    }, 1500);
    return () => window.clearTimeout(id);
  }, [success, navigate, t]);

  const submit = async () => {
    if (!accepted) {
      setError(t("Please accept the Trial Terms to continue."));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await startTrial({
        acceptedTrialTerms: true,
        industry: industry.trim() || undefined,
        size: size || undefined,
        reference: reference || undefined,
      });
      if (res.errors && res.errors.length > 0) {
        setError(res.errors[0]?.message || res.errors[0]?.description || t("Couldn't start your trial. Please try again."));
        setSubmitting(false);
        return;
      }
      track(AnalyticsEvents.TrialStarted, { source: "start_trial_page" });
      setSuccess(true);
    } catch (e: unknown) {
      const apiErr =
        (e as { errors?: ApiError[] })?.errors?.[0] ||
        (e as { response?: { data?: { errors?: ApiError[] } } })?.response?.data?.errors?.[0];
      setError(
        apiErr?.description || apiErr?.message || t("Couldn't start your trial. Please try again."),
      );
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-10">
        <div className="text-center max-w-md">
          <div
            className="mx-auto w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 flex items-center justify-center"
            style={{ animation: "mz-pop 360ms cubic-bezier(0.22, 1, 0.36, 1) both" }}
          >
            <Sparkles size={28} />
          </div>
          <style>{`@keyframes mz-pop { from { opacity: 0; transform: scale(0.9) } to { opacity: 1; transform: scale(1) } }`}</style>
          <h1 className="mt-5 text-2xl font-semibold tracking-tight text-foreground">
            {t("You're on a 30-day trial.")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("Every module is unlocked. We'll remind you well before it ends.")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background">
      <Toaster position={isRtl ? "top-left" : "top-right"} />
      <TermsDialog
        open={termsOpen}
        onOpenChange={setTermsOpen}
        kind="TrialUpgrade"
        autoAccept={false}
        onAccepted={() => setAccepted(true)}
      />

      <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-start gap-3 mb-7">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 flex items-center justify-center">
            <Rocket size={20} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              {t("Upgrade to a free 30-day trial")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground max-w-[60ch]">
              {t("Unlock every module across the platform for the next 30 days. No card, no commitment.")}
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="px-6 py-5 border-b border-border">
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              <Benefit icon={<Layers size={14} />} text={t("Unlock every module on the platform")} />
              <Benefit icon={<Users size={14} />} text={t("Bring your entire team along for the trial")} />
              <Benefit icon={<CreditCard size={14} />} text={t("No credit card required")} />
              <Benefit icon={<Bell size={14} />} text={t("We'll remind you well before it ends")} />
            </ul>
          </div>

          <div className="px-6 py-5 space-y-4">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t("Help us tailor it (optional)")}
            </p>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground/90">{t("Industry")}</label>
                <Input
                  list="mz-industry-list"
                  value={industry}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIndustry(e.target.value)}
                  placeholder={t("e.g. Hospitality, Construction, Retail…")}
                />
                <datalist id="mz-industry-list">
                  <option value="Hospitality" />
                  <option value="Construction" />
                  <option value="Retail" />
                  <option value="Healthcare" />
                  <option value="Education" />
                  <option value="Manufacturing" />
                  <option value="Logistics" />
                  <option value="Professional Services" />
                  <option value="Technology" />
                  <option value="Public Sector" />
                </datalist>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground/90">{t("Team size")}</label>
                <Select
                  value={size}
                  options={SIZE_OPTIONS}
                  onChange={(v: string) => setSize(v)}
                  placeholder={t("Select team size")}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground/90">{t("How did you hear about us?")}</label>
              <Select
                value={reference}
                options={referralOptions}
                onChange={(v: string) => setReference(v)}
                placeholder={t("Choose one")}
              />
            </div>
          </div>

          <div className="px-6 py-5 border-t border-border bg-muted/30 space-y-3">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <Checkbox
                checked={accepted}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setAccepted(e.target.checked)
                }
              />
              <span className="text-xs leading-relaxed text-foreground/90 -mt-0.5">
                {t("I accept the Mowazaf ")}
                <button
                  type="button"
                  onClick={() => setTermsOpen(true)}
                  className="text-primary underline underline-offset-2 hover:opacity-80"
                >
                  {t("Trial Terms")}
                </button>
                .
              </span>
            </label>

            {error && (
              <div
                role="alert"
                className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300"
              >
                {error}
              </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-1">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                {t("Cancel")}
              </Button>
              <Button onClick={submit} loading={submitting} disabled={!accepted} size="lg">
                {t("Start my 30-day trial")}
                <ArrowRight size={16} className="ms-2" />
              </Button>
            </div>
          </div>
        </div>

        <p className="mt-5 text-center text-xs text-muted-foreground">
          {t("You can downgrade or revoke any time. We'll never auto-charge you.")}
        </p>
      </div>
    </div>
  );
};

interface BenefitProps {
  icon: React.ReactNode;
  text: string;
}
const Benefit: React.FC<BenefitProps> = ({ icon, text }) => (
  <li className="flex items-start gap-2.5">
    <span className="shrink-0 w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 flex items-center justify-center mt-0.5">
      {icon}
    </span>
    <span className="text-sm text-foreground/90">{text}</span>
  </li>
);

export default StartTrialPage;
